import { create } from 'zustand';
import { MOCK_CURRENT_USER_PROFILE } from '@/features/profile/mockData';
import { getAccountTypeLabel } from '@/features/profile/accountTypeLabel';
import type { UserProfile } from '@/features/profile/types';
import { createDefaultProfileEditDraft } from '@/features/profile/edit/profileEditSeed';
import type {
  ProfileEditDraft,
  ProfileEditFormValues,
  ProfileFieldLocks,
} from '@/features/profile/edit/types';
import {
  profileEditToDisplayName,
  profileEditToInitials,
  splitFullName,
} from '@/features/profile/edit/profileEditUtils';
import { useAuthStore } from '@/store/authStore';

interface ProfileEditState {
  draft: ProfileEditDraft;
  isDirty: boolean;
  /** True once the draft has been seeded from the live API profile. */
  hydratedFromServer: boolean;
  setField: <K extends keyof ProfileEditFormValues>(
    key: K,
    value: ProfileEditFormValues[K],
  ) => void;
  resetDraft: () => void;
  commitDraft: () => void;
  /** Replace the draft with values pulled from the live API profile payload. */
  hydrateFromProfile: (profile: UserProfile, raw?: Record<string, unknown>) => void;
}

function revokeIfBlob(url: string | undefined): void {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

function draftToFormValues(draft: ProfileEditDraft): ProfileEditFormValues {
  const { fieldLocks: _locks, ...values } = draft;
  return values;
}

export const useProfileEditStore = create<ProfileEditState>((set, get) => ({
  draft: createDefaultProfileEditDraft(),
  isDirty: false,
  hydratedFromServer: false,

  setField: (key, value) => {
    set((state) => ({
      draft: { ...state.draft, [key]: value },
      isDirty: true,
    }));
  },

  resetDraft: () => {
    const current = get().draft;
    revokeIfBlob(current.avatarUrl);
    revokeIfBlob(current.bannerUrl);
    set({
      draft: createDefaultProfileEditDraft(),
      isDirty: false,
      hydratedFromServer: false,
    });
  },

  commitDraft: () => {
    const { draft } = get();
    const name = profileEditToDisplayName(draft);
    const authUser = useAuthStore.getState().user;
    const rawUser = useAuthStore.getState().rawUser;
    const token = useAuthStore.getState().token;
    if (authUser && token) {
      useAuthStore.getState().setAuth(
        {
          ...authUser,
          name,
          email: draft.email.trim(),
          username: draft.username.trim() || authUser.username,
          schoolName: draft.institution.trim() || authUser.schoolName,
          avatarUrl: draft.avatarUrl ?? authUser.avatarUrl,
        },
        token,
        rawUser ?? undefined,
      );
    }
    set({ isDirty: false });
  },

  hydrateFromProfile: (profile, raw) => {
    const { firstName, lastName } = splitFullName(profile.name);
    const rawData = raw ?? {};
    const pickStr = (...keys: string[]): string | undefined => {
      for (const key of keys) {
        const value = rawData[key];
        if (typeof value === 'string' && value.trim()) return value;
        if (typeof value === 'number') return String(value);
      }
      return undefined;
    };
    /**
     * Convert the backend's `dob` (epoch milliseconds) into the `YYYY-MM-DD`
     * format expected by the `<input type="date">` control.
     *
     * The conversion is done in **UTC** so the displayed date matches the
     * calendar date the backend stored (avoids the off-by-one shift that
     * happens when local time zones are west of UTC and `dob` is stored at
     * 00:00 UTC).
     */
    const formatUtcDate = (ms: number): string => {
      const d = new Date(ms);
      if (Number.isNaN(d.getTime())) return '';
      const yyyy = d.getUTCFullYear().toString().padStart(4, '0');
      const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
      const dd = d.getUTCDate().toString().padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const pickDateOfBirth = (): string => {
      for (const key of ['dateOfBirth', 'dob', 'birthDate'] as const) {
        const value = rawData[key];
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
          const formatted = formatUtcDate(value);
          if (formatted) return formatted;
        }
        if (typeof value === 'string' && value.trim()) {
          // Already a date string (YYYY-MM-DD or ISO) — keep just the date part.
          return value.slice(0, 10);
        }
      }
      return '';
    };
    const current = get().draft;
    revokeIfBlob(current.avatarUrl);
    revokeIfBlob(current.bannerUrl);

    const next: ProfileEditDraft = {
      firstName,
      lastName,
      username: profile.username ?? '',
      institution: profile.institution ?? '',
      bio: pickStr('bio', 'aboutMe', 'description') ?? '',
      email: profile.email ?? '',
      mobile: profile.mobileNo ?? pickStr('mobileNo', 'mobileNumber', 'phone') ?? '',
      gender: ((): ProfileEditDraft['gender'] => {
        const g = (pickStr('gender') ?? '').toLowerCase();
        if (g === 'male' || g === 'm') return 'male';
        if (g === 'female' || g === 'f') return 'female';
        if (g === 'non_binary' || g === 'non-binary') return 'non_binary';
        return 'prefer_not_to_say';
      })(),
      dateOfBirth: pickDateOfBirth(),
      avatarUrl: profile.avatarUrl,
      bannerUrl: profile.bannerUrl,
      fieldLocks: current.fieldLocks,
    };

    set({ draft: next, isDirty: false, hydratedFromServer: true });
  },
}));

export function mapProfileEditDraftToUserProfile(draft: ProfileEditDraft): UserProfile {
  const base = MOCK_CURRENT_USER_PROFILE;
  const authUser = useAuthStore.getState().user;
  const name = profileEditToDisplayName(draft);

  return {
    ...base,
    id: authUser?.id ?? base.id,
    name,
    username: draft.username.trim(),
    email: draft.email.trim(),
    institution: draft.institution.trim(),
    bio: draft.bio.trim() || undefined,
    accountTypeLabel: getAccountTypeLabel(base.accountType),
    initials: profileEditToInitials(draft),
    avatarUrl: draft.avatarUrl,
    bannerUrl: draft.bannerUrl,
  };
}

export function selectProfileEditForm(draft: ProfileEditDraft): ProfileEditFormValues {
  return draftToFormValues(draft);
}

export function selectProfileFieldLocks(draft: ProfileEditDraft): ProfileFieldLocks {
  return draft.fieldLocks;
}
